import { zodResolver } from '@hookform/resolvers/zod';
import { subDays } from 'date-fns';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/hooks';
import {
  AccountMultiSelect,
  DonorMultiSelect,
  SitesMultiSelect,
  YardsMultiSelect,
} from '@/components';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { DonorSchedule } from '@/rtk-query.gql';

import { CLINIC_RANCH_HAUL_IN_OPTIONS } from '../../constants';
import {
  type DonorToDoReportFormDataInput,
  type DonorToDoReportFormDataOutput,
  donorToDoReportSchema,
} from './schema';

export function DonorToDoReportDialog({
  visible,
  onHide,
  onSubmit,
}: {
  visible: 'pdf' | 'excel' | undefined;
  onHide: () => void;
  onSubmit: (params: DonorToDoReportFormDataOutput) => void;
}) {
  const { t } = useTranslation();
  const { selectedDonorSchedules } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const {
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
    reset,
  } = useForm<
    DonorToDoReportFormDataInput,
    unknown,
    DonorToDoReportFormDataOutput
  >({
    resolver: zodResolver(donorToDoReportSchema),
    defaultValues: {
      startDate: undefined,
      endDate: undefined,
      siteIds: [],
      donorIds: [],
      clinicIds: [],
      accountIds: [],
      yardIds: [],
    },
  });

  const watchedValues = watch();

  const initializeFormData = () => {
    if (!selectedDonorSchedules.length) return;

    const firstScheduleDate = selectedDonorSchedules[0].date;
    if (!firstScheduleDate) return;

    const referenceDate = new Date(firstScheduleDate);
    const expectedStartDate = subDays(referenceDate, 8);
    const expectedEndDate = subDays(referenceDate, 1);

    const uniqueDonors = new Map();
    const uniqueAccounts = new Map();
    const uniqueYards = new Map();
    const uniqueSites = new Map();

    selectedDonorSchedules.forEach((schedule: DonorSchedule) => {
      if (schedule.donor) {
        uniqueDonors.set(schedule.donor.id, schedule.donor);

        if (schedule.donor.yard) {
          uniqueYards.set(schedule.donor.yard.id, schedule.donor.yard);
        }
      }
      if (schedule.owner) {
        uniqueAccounts.set(schedule.owner.id, schedule.owner);
      }
      if (schedule.aspirationSite) {
        uniqueSites.set(schedule.aspirationSite.id, schedule.aspirationSite);
      }
    });

    reset({
      startDate: expectedStartDate,
      endDate: expectedEndDate,
      siteIds: Array.from(uniqueSites.values()).map((site: any) => site.id),
      donorIds: Array.from(uniqueDonors.values()).map((donor: any) => donor.id),
      clinicIds: CLINIC_RANCH_HAUL_IN_OPTIONS.map(
        (option) => option.value,
      ) as number[],
      accountIds: Array.from(uniqueAccounts.values()).map(
        (account: any) => account.id,
      ),
      yardIds: Array.from(uniqueYards.values()).map((yard: any) => yard.id),
    });
  };

  const onFormSubmit = (data: DonorToDoReportFormDataOutput) => {
    onSubmit({
      startDate: data.startDate,
      endDate: data.endDate,
      siteIds: data.siteIds,
      donorIds: data.donorIds,
      clinicIds: data.clinicIds,
      accountIds: data.accountIds,
      yardIds: data.yardIds,
    });

    onHide();
  };

  const handleDialogShow = () => {
    initializeFormData();
  };

  const footer = (
    <div>
      <Button
        label={t('common.cancel')}
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
      />
      <Button
        label={t('common.generateReport')}
        icon="pi pi-check"
        onClick={handleSubmit(onFormSubmit)}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header={t('donorReport.donorToDoReportParameters')}
      visible={!!visible}
      onHide={onHide}
      footer={footer}
      className="w-[500px]"
      onShow={handleDialogShow}
    >
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="flex flex-col gap-4"
      >
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="mb-2 block">
              {t('common.startDateRequired')}
            </label>
            <Calendar
              value={watchedValues.startDate}
              onChange={(e) => setValue('startDate', e.value as Date)}
              showIcon
              className={`w-full ${errors.startDate ? 'p-invalid' : ''}`}
            />
            {errors.startDate && (
              <small className="p-error">{errors.startDate.message}</small>
            )}
          </div>
          <div className="flex-1">
            <label className="mb-2 block">{t('common.endDateRequired')}</label>
            <Calendar
              value={watchedValues.endDate}
              onChange={(e) => setValue('endDate', e.value as Date)}
              showIcon
              className={`w-full ${errors.endDate ? 'p-invalid' : ''}`}
            />
            {errors.endDate && (
              <small className="p-error">{errors.endDate.message}</small>
            )}
          </div>
        </div>

        <div>
          <Controller
            control={control}
            name="siteIds"
            render={({ field: { onChange, value } }) => (
              <SitesMultiSelect
                label="Sites"
                value={value}
                onChange={onChange}
                appendTo={null}
              />
            )}
          />
          {errors.siteIds && (
            <small className="p-error">{errors.siteIds.message}</small>
          )}
        </div>

        <div>
          <Controller
            control={control}
            name="donorIds"
            render={({ field: { onChange, value } }) => (
              <DonorMultiSelect
                label="Donors"
                value={value}
                onChange={onChange}
                appendTo={null}
              />
            )}
          />
          {errors.donorIds && (
            <small className="p-error">{errors.donorIds.message}</small>
          )}
        </div>

        <div>
          <label className="mb-2 block">{t('common.clinics')}</label>
          <Controller
            control={control}
            name="clinicIds"
            render={({ field: { onChange, value } }) => (
              <MultiSelect
                value={value}
                options={CLINIC_RANCH_HAUL_IN_OPTIONS}
                onChange={(e: { value: number[] }) => {
                  onChange(e.value);
                }}
                display="chip"
                optionLabel="name"
                optionValue="value"
                placeholder={t('common.selectClinics')}
                className={`w-full ${errors.clinicIds ? 'p-invalid' : ''}`}
              />
            )}
          />
          {errors.clinicIds && (
            <small className="p-error">{errors.clinicIds.message}</small>
          )}
        </div>

        <div>
          <Controller
            control={control}
            name="accountIds"
            render={({ field: { onChange, value } }) => (
              <AccountMultiSelect
                label="Accounts"
                value={value}
                onChange={onChange}
                appendTo={null}
              />
            )}
          />
          {errors.accountIds && (
            <small className="p-error">{errors.accountIds.message}</small>
          )}
        </div>

        <div>
          <Controller
            control={control}
            name="yardIds"
            render={({ field: { onChange, value } }) => (
              <YardsMultiSelect
                label="Yards"
                value={value}
                onChange={onChange}
                appendTo={null}
              />
            )}
          />
          {errors.yardIds && (
            <small className="p-error">{errors.yardIds.message}</small>
          )}
        </div>
      </form>
    </Dialog>
  );
}
